import * as React from "react";
import { LocalizedDateTime, DateTimeAccuracy } from "../../misc/components/LocalizedDateTime";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { SpirometryResults, StudyRelevantParameters, ArtifactAcceptability, ArtifactAcceptabilityIds } from "../../PulmonaryFunctionTests/Shared/PFTAPITypes";
import { ToggleTestAcceptabilityAction } from '../Shared/UseAcceptability';
import { ToggleTestGraphVisibilityAction, GraphState } from '../Shared/UseGraphs';
import { NextGenPftResultParameters, NextGenParameterResult } from "./Testing/NextGenPftResultParameters";
import { FvcSession, FvcAnalysisSet, FvcTest } from "webportalclient";
import { NextGenQualityAcceptability } from "./Testing/NextGenQualityAcceptability";
import { AcceptabilityNextGenState } from "../Shared/UseAcceptabilityNextGen";
import { NextGenTestAcceptability } from "./Testing/NextGenTestAcceptability";
import { ParameterType } from "../../misc/api/ParameterTypes";
import { NextGenRepeatabilityTable } from "./Testing/NextGenRepeatabilityTable";
import { ResultsContext } from "./UseResultsContext";
import { OverreadingResultsComments } from "../Shared/OverreadingResultsComments";
import { NextGenFixedCommentsState } from "./Testing/UseNextGenFixedComments";
import { DynamicResultsGroupHeading } from "../Shared/ResultsGroupHeading";

export interface SpirometryResultsTableProps {
    session: FvcSession;
    analysisSet: FvcAnalysisSet;
    isSiteAnalysisSet: boolean;
    siteAnalysisSet: FvcAnalysisSet;
    isViewOnly: boolean;
    fixedCommentsStateOverreading: NextGenFixedCommentsState;
    fixedCommentsDispatchOverreading;
    fixedCommentsStateTechnician: NextGenFixedCommentsState;
    fixedCommentsDispatchTechnician;
    acceptabilityState: AcceptabilityNextGenState;
    acceptabilityDispatch;
    graphState: GraphState;
    technicianSessionCommentsAvailable: boolean;
    technicianTestCommentsAvailable: boolean;
    graphDispatch;
    studyFvcRelevantParameters?: StudyRelevantParameters;
}

export const SpirometryResultsTable = (props: SpirometryResultsTableProps) => {
    if (props.analysisSet) {
        return (
            <ResultsContext.Provider value={{ showBestValues: true, showPredictedValues: false, showPercentagePredicted: false }}>
                <div style={{ overflow: "scroll", maxWidth: "100%" }}>
                    <table id="ResultsTable" className="table table-striped">
                        <thead>
                            <tr>
                                <th />
                                {props.graphState.graphData.sort((a, b) => a.TestNumber - b.TestNumber).map(t => (
                                    <th
                                        key={`TestHeader${t.TestId}`}
                                        style={{ background: t.ColorHex, color: "#fff" }}
                                    >
                                        {t.TestNumber}
                                    </th>
                                ))}
                                <th>Best</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Display</td>
                                {props.graphState.graphData.sort((a, b) => a.TestNumber - b.TestNumber).map(t => (
                                    <td key={`TestVisibilityToggle${t.TestId}`}>
                                        <input
                                            checked={t.IsDisplayed}
                                            type="checkbox"
                                            onChange={() =>
                                                props.graphDispatch(
                                                    ToggleTestGraphVisibilityAction.create(t.TestId)
                                                )
                                            }
                                        />
                                    </td>
                                ))}
                                <td />
                            </tr>
                            <tr>
                                <td>Time</td>
                                {props.analysisSet.tests.sort((a, b) => a.test.index - b.test.index).map(t => (
                                    <td key={`TestDateTime${t.testId}`}>
                                        <LocalizedDateTime
                                            time={t.test.occurred}
                                            accuracy={DateTimeAccuracy.TimeOnly}
                                        />
                                    </td>
                                ))}
                                <td />
                            </tr>

                            <NextGenPftResultParameters
                                session={props.session}
                                analysisSet={props.analysisSet}
                            >
                                <>
                                    <DynamicResultsGroupHeading resultsCount={props.analysisSet.tests.length} title="Parameters" />
                                    <NextGenParameterResult name="VC (L)" identifier={ParameterType.Vc} value={(t) => t.vc} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="FVC (L)" identifier={ParameterType.Fvc} value={(t) => t.fvc} bestValue={(a) => a.bestFvc} predictedValue={(s) => s.predictedFvc} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="FEV1 (L)" identifier={ParameterType.Fev1} value={(t) => t.fev1} bestValue={(a) => a.bestFev1} predictedValue={(s) => s.predictedFev1} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="FEV1/VC" identifier={ParameterType.Fev1Vc} value={(t) => t.fev1Vc} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="FEV1 Ratio" identifier={ParameterType.Fev1R} value={(t) => t.fev1R} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="FEV1/FVC" identifier={ParameterType.Fev1Fvc} value={(t) => t.fev1Fvc} bestValue={(a) => a.bestFev1Fvc} predictedValue={(a) => a.predictedFev1Fvc} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="FEV6 (L)" identifier={ParameterType.Fev6} value={(t) => t.fev6} bestValue={() => null} predictedValue={(s) => s.predictedFev6} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="PEF (L/sec)" identifier={ParameterType.PefSec} value={(t) => t.pefSec} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="PEF (L/min)" identifier={ParameterType.PefMin} value={(t) => t.pefMin} bestValue={(a) => a.bestPefMin} predictedValue={(s) => s.predictedPefMin} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="FIVC (L)" identifier={ParameterType.Fivc} value={(t) => t.fivc} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="PIF (L/sec)" identifier={ParameterType.PifSec} value={(t) => t.pifSec} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="PIF (L/min)" identifier={ParameterType.PifMin} value={(t) => t.pifMin} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="FET (s)" identifier={ParameterType.Fet} value={(t) => t.fet} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="BEV (L)" identifier={ParameterType.Bev} value={(t) => t.bev} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="TV (L)" identifier={ParameterType.Tv} value={(t) => t.tv} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="IRV (L)" identifier={ParameterType.Irv} value={(t) => t.irv} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="ERV (L)" identifier={ParameterType.Erv} value={(t) => t.erv} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="IC (L)" identifier={ParameterType.Ic} value={(t) => t.ic} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="TExt (s)" identifier={ParameterType.Text} value={(t) => t.tExt} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="TPEF (ms)" identifier={ParameterType.Tpef} value={(t) => t.tPef} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="FEF 75 (L/s)" identifier={ParameterType.Fef75} value={(t) => t.fef75} bestValue={(s) => s.bestFef75} predictedValue={(s) => s.predictedFef75} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="FEF 25-75 (L/s)" identifier={ParameterType.Fef2575} value={(t) => t.fef2575} bestValue={(a) => a.bestFef2575} predictedValue={(s) => s.predictedFef2575} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="FEV1/FEV6" identifier={ParameterType.Fev1Fev6} value={(t) => t.fev1Fev6} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedAnalysisParameters" />
                                    <NextGenParameterResult name="tRISE (ms)" identifier={ParameterType.RiseTime} value={(t) => t.tRise} bestValue={() => null} predictedValue={() => null} parameterAudience="SelectedAnalysisParameters" />
                                </>
                            </NextGenPftResultParameters>
                            <NextGenRepeatabilityTable analysisSet={props.analysisSet} session={props.session} />
                            <NextGenQualityAcceptability session={props.session} />
                            <NextGenTestAcceptability
                                viewOnly={props.isViewOnly}
                                loading={props.acceptabilityState.loadingBestValues}
                                analysisSet={props.acceptabilityState.analysisSet}
                                session={props.session}
                                acceptabilityState={props.acceptabilityState}
                                comparisonAnalysisSet={props.siteAnalysisSet}
                                onAcceptabilityChange={(id, checked) => props.acceptabilityDispatch(ToggleTestAcceptabilityAction.create(id, checked))} />
                            <OverreadingResultsComments
                                viewOnly={props.isViewOnly}
                                session={props.session}
                                fixedCommentsDispatchTechnician={props.fixedCommentsDispatchTechnician}
                                fixedCommentsStateTechnician={props.fixedCommentsStateTechnician}
                                fixedCommentsDispatchOverreading={props.fixedCommentsDispatchOverreading}
                                fixedCommentsStateOverreading={props.fixedCommentsStateOverreading}
                            />
                        </tbody>
                    </table>
                </div>
            </ResultsContext.Provider>
        );
    } else {
        return <LoadingWithMessage message="Loading Spirometry Results..." />;
    }
}

export const MapToLegacy = (sessionId: string, analysisSet: FvcAnalysisSet): SpirometryResults => {
    const toArtifactAcceptability = (artifact: string) => {
        switch (artifact) {
            case ArtifactAcceptabilityIds.Undetermined:
                return ArtifactAcceptability.Undetermined;
            case ArtifactAcceptabilityIds.Acceptable:
                return ArtifactAcceptability.Acceptable;
            case ArtifactAcceptabilityIds.Usable:
                return ArtifactAcceptability.Usable;
            case ArtifactAcceptabilityIds.NotAcceptable:
                return ArtifactAcceptability.NotAcceptable;
        }
    }

    return {
        TestResults: analysisSet.tests.map(ast => ({
            TestDateTime: ast.test.occurred,
            TestNumber: ast.test.index,
            TestId: ast.testId,
            OverreaderAcceptability: ast.accepted,
            StartOfTestQuality: toArtifactAcceptability(ast.test.startOfTestQuality),
            EofeQuality: toArtifactAcceptability(ast.test.eofeQuality),
            TestDurationQuality: toArtifactAcceptability(ast.test.testDurationQuality),
            FlowPlateauQuality: toArtifactAcceptability(ast.test.flowPlateauQuality),
            CoughFreeQuality: toArtifactAcceptability(ast.test.coughFreeQuality),
            FivcFvcQuality: toArtifactAcceptability(ast.test.fivcFvcQuality),
            Fev1Quality: toArtifactAcceptability(ast.test.fev1Quality),
            FvcQuality: toArtifactAcceptability(ast.test.fvcQuality),
            TestParameters: [],
            RepeatabilityParameters: []
        })),
        BestParameters: [],
        DisplayParameters: [],
        DisplayRepeatabilityParameters: [],
        SessionId: sessionId,
        AnalysisSetId: analysisSet.id
    }
}

export default SpirometryResultsTable;
